const prefixMatch = (pathname = '', prefixes = []) => {
  const path = String(pathname || '').trim() || '/';
  return prefixes.some(prefix => path === prefix || path.startsWith(`${prefix}/`));
};

const surface = ({
  id,
  room = '',
  name,
  route,
  navigationGroup = '',
  verb = '',
  orientation = '',
  activePrefixes = [route],
  authenticatedPrefixes = activePrefixes,
  match
}) => Object.freeze({
  schemaVersion: 1,
  id,
  kind: 'surface',
  name,
  version: '1.0.0',
  description: room
    ? 'A primary Noeis knowledge surface.'
    : 'An operational Noeis surface.',
  sourcePath: 'note-taker-ui/src/system/noeisSurfaceDefinitions.js',
  room,
  route,
  navigationGroup,
  verb,
  orientation,
  activePrefixes: Object.freeze([...activePrefixes]),
  authenticatedPrefixes: Object.freeze([...authenticatedPrefixes]),
  match: match || ((location = {}) => prefixMatch(location.pathname, activePrefixes))
});

/**
 * The sole declarative authority for product surfaces, their navigation
 * projection, room identity, and authenticated route ownership. React route
 * elements remain in App.js because the registry is metadata, not execution.
 */
export const NOEIS_SURFACE_DEFINITIONS = Object.freeze([
  surface({
    id: 'surface.library',
    room: 'library',
    name: 'Library',
    route: '/library',
    navigationGroup: 'primary',
    verb: 'Read',
    orientation: 'Recover source material and understand where it came from.',
    authenticatedPrefixes: ['/library', '/articles', '/all-highlights', '/tags', '/collections', '/views', '/search', '/export']
  }),
  /* The books you read with the desk open beside them. Every mark made there
     is a highlight in the Library like any other, so Books sits beside it:
     a shelf of the Library's own sources, given a door of its own. */
  surface({
    id: 'surface.books',
    name: 'Books',
    route: '/books',
    navigationGroup: 'primary',
    verb: 'Read',
    orientation: 'Read a book with one line typed beside it.'
  }),
  surface({
    id: 'surface.think',
    room: 'think',
    name: 'Think',
    route: '/think',
    navigationGroup: 'primary',
    verb: 'Develop',
    orientation: 'Work an unfinished idea without pretending it is settled.',
    authenticatedPrefixes: ['/think', '/concepts', '/concept', '/notebook', '/questions', '/question', '/board', '/boards', '/studio-board', '/map', '/review', '/return-queue']
  }),
  surface({
    id: 'surface.wiki',
    room: 'wiki',
    name: 'Wiki',
    route: '/wiki',
    navigationGroup: 'primary',
    verb: 'Keep',
    orientation: 'Read and maintain knowledge you have chosen to keep.',
    activePrefixes: ['/', '/wiki', '/paper'],
    authenticatedPrefixes: ['/wiki', '/paper', '/today'],
    match: ({ pathname = '' } = {}) => pathname === '/' || prefixMatch(pathname, ['/wiki', '/paper'])
  }),
  /* Papers your agents maintain for you.

     It sits after Wiki and belongs to it: an edition is a thing the wiki
     received rather than a room of its own work, and the four rooms are what
     you did. It was in the More menu, which was honest about that but meant
     nobody found it — and a feature nobody can find cannot be tested against
     a real agent. Named in the bar until it has earned or lost its place. */
  surface({
    id: 'surface.editions',
    name: 'Editions',
    route: '/editions',
    navigationGroup: 'primary',
    verb: 'Read',
    orientation: 'Papers your agents keep for you, filed through the wiki MCP.',
    activePrefixes: ['/editions'],
    authenticatedPrefixes: ['/editions']
  }),
  surface({
    id: 'surface.judgment',
    room: 'judgment',
    name: 'Judgment',
    route: '/judgment',
    navigationGroup: 'primary',
    verb: 'Decide',
    orientation: 'Make and revisit consequential calls against their evidence.',
    authenticatedPrefixes: ['/judgment', '/mirror', '/welcome']
  }),
  /* Private writing for your job: meetings, follow-ups, the week. It is a
     room you walk into from the bar, but no agent or Partner works in it. */
  surface({
    id: 'surface.work',
    name: 'Work',
    route: '/work',
    navigationGroup: 'primary',
    verb: 'Work',
    orientation: 'Private notes for meetings, follow-ups and the week.'
  }),
  surface({
    id: 'surface.connections',
    name: 'Connections',
    route: '/connections#sources',
    navigationGroup: 'utility',
    activePrefixes: ['/connections', '/integrations', '/data-integrations'],
    authenticatedPrefixes: ['/connections', '/integrations', '/data-integrations']
  }),
  surface({
    id: 'surface.settings',
    name: 'Settings',
    route: '/settings',
    navigationGroup: 'utility'
  }),
  surface({
    id: 'surface.growth',
    name: 'Growth',
    route: '/marketing-analytics',
    activePrefixes: ['/marketing-analytics', '/search-console-opportunities'],
    authenticatedPrefixes: ['/marketing-analytics', '/search-console-opportunities']
  }),
  surface({
    id: 'surface.how-to-use',
    name: 'How To Use',
    route: '/how-to-use',
    navigationGroup: 'secondary'
  })
]);

const BY_ID = new Map(NOEIS_SURFACE_DEFINITIONS.map(item => [item.id, item]));
const BY_ROOM = new Map(NOEIS_SURFACE_DEFINITIONS.filter(item => item.room).map(item => [item.room, item]));

export const getNoeisSurfaceDefinition = (id = '') => BY_ID.get(String(id || '').trim()) || null;

export const getNoeisRoomDefinition = (room = '') => BY_ROOM.get(String(room || '').trim()) || null;

export const resolveNoeisSurfaceDefinition = (pathname = '') => (
  NOEIS_SURFACE_DEFINITIONS.find(item => item.room && item.match({
    pathname: String(pathname || '').split(/[?#]/)[0]
  })) || null
);

export const getNoeisNavigationDefinitions = (group = '') => (
  NOEIS_SURFACE_DEFINITIONS.filter(item => item.navigationGroup === group)
);

export const getAuthenticatedRoutePrefixes = () => Object.freeze([
  ...new Set(NOEIS_SURFACE_DEFINITIONS.flatMap(item => item.authenticatedPrefixes))
]);

export default NOEIS_SURFACE_DEFINITIONS;
